import React from "react";
import { Link, useLocation } from "react-router-dom";
import { sessionsAndFees } from "../mock/mockData";
import { useCurrency, BASE_EXAM_FEE_EUR, PREMIUM_CERT_FEE_EUR } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function FeesSchedule() {
  const location = useLocation();
  const fromDashboard = location.state?.fromDashboard || document.referrer.includes('/dashboard');
  const { formatPrice } = useCurrency();

  return (
    <div className="space-y-8">
      {fromDashboard && (
        <Link to="/dashboard">
          <Button variant="outline" size="sm" className="border-[#d5c39d] text-[#182033] hover:bg-[#f5ebd9]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </Link>
      )}

      <section className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Fees &amp; Examination Schedule</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-700 sm:text-base">
          Examinations are organised into defined sessions across the year. Fee structures take account of examination mode, instrument, level and administrative requirements.
        </p>
      </section>

      {/* Exam Fees Section */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Examination Fees</h2>
        
        {/* Standard Instrument Fees */}
        <div className="rounded-2xl border border-[#c9a227]/40 bg-gradient-to-br from-[#fffdf5] to-[#f9f2e6] p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-[#6b6a66]">Per Level Certification Fee</p>
              <p className="text-3xl font-bold text-[#182033]">{formatPrice(BASE_EXAM_FEE_EUR)}</p>
              <p className="text-xs text-[#8c7648]">Guitar • Piano • Drums • Violin • Vocals • Alto Saxophone</p>
            </div>
          </div>
          <div className="border-t border-[#e1d2b3] pt-4 space-y-2 text-sm text-[#4b4a46]">
            <p>• Fee applies to each level examination (Level 1 through Level 6)</p>
            <p>• Same fee structure for all standard instruments</p>
            <p>• Includes examination, assessment, and certificate upon passing</p>
            <p>• Video submission and live examination have the same fee</p>
          </div>
        </div>

        {/* Premium Certification Fees */}
        <div className="rounded-2xl border border-[#182033]/30 bg-gradient-to-br from-[#182033] to-[#1e2a42] p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-[#d5c39d]/80">Professional Certification Fee</p>
              <p className="text-3xl font-bold text-[#d5c39d]">{formatPrice(PREMIUM_CERT_FEE_EUR)}</p>
              <p className="text-xs text-[#d5c39d]/70">Premium Professional Certifications</p>
            </div>
          </div>
          <div className="border-t border-[#d5c39d]/30 pt-4 space-y-3">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-2 w-2 rounded-full bg-[#d5c39d]"></span>
              <div>
                <p className="font-medium text-[#f5ebd9]">AI Voice Model Trainer & Voiceover Artist Master Certification</p>
                <p className="text-sm text-[#d5c39d]/70">Comprehensive certification in AI voice technology and professional voiceover artistry</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-2 w-2 rounded-full bg-[#d5c39d]"></span>
              <div>
                <p className="font-medium text-[#f5ebd9]">Professional Music Therapy Certification</p>
                <p className="text-sm text-[#d5c39d]/70">In-depth training in therapeutic music applications and clinical practice</p>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-[#d5c39d]/20 text-sm text-[#d5c39d]/80">
            <p>• Single comprehensive examination covering all certification modules</p>
            <p>• Includes professional certification upon successful completion</p>
          </div>
        </div>
      </section>

      <section className="space-y-4 text-sm text-slate-700">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Examination sessions</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {sessionsAndFees.sessions.map((session) => (
            <div
              key={session.name}
              className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm backdrop-blur-xl"
            >
              <p className="text-sm font-semibold text-slate-900">{session.name}</p>
              <p className="mt-1 text-sm text-slate-700">{session.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3 text-sm text-slate-700">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Additional Notes</h2>
        <ul className="space-y-1">
          {sessionsAndFees.notes.map((note) => (
            <li key={note}>• {note}</li>
          ))}
        </ul>
      </section>

      {/* Important Fee Policy Section */}
      <section className="rounded-2xl border border-red-200 bg-red-50 p-5 shadow-sm">
        <h2 className="text-base font-semibold tracking-tight text-red-800 mb-3">Important Fee Policy</h2>
        <div className="space-y-3 text-sm text-red-700">
          <div className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-red-500" />
            <p><strong>Non-Refundable:</strong> All examination fees are strictly non-refundable. Once payment is made, the fees cannot be refunded under any circumstances.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-red-500" />
            <p><strong>Non-Transferable:</strong> Examination fees cannot be transferred to another candidate or a different examination.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-red-500" />
            <p><strong>No Cancellation:</strong> Once an examination is booked and the fee is paid, it cannot be cancelled.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-amber-600" />
            <p><strong>Rescheduling:</strong> Examinations may be rescheduled <strong>only once</strong>, and must be done at least 72 hours before the scheduled exam time. No further date changes will be permitted after rescheduling.</p>
          </div>
        </div>
        <p className="text-xs text-red-600 mt-4 pt-3 border-t border-red-200">
          Please ensure you are fully prepared and have selected the correct exam date before completing your payment.
        </p>
      </section>
    </div>
  );
}
